import type { FilterMainComponentProps, HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import type { Filter, FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import { getRangeRoundTo } from '@/app/common/filters/types/range/rangeData.ts';
import { scaleRangeData, usePriceFilterScale } from '@/app/common/filters/surfaces/forms/custom/usePriceFilterScale.ts';

type ScaledRangeFilter = {
  filter: Filter;
  handleFilterChanged: HandleFilterChanged;
  roundTo?: number;
};

// The range in the toggler's display unit: a ×pax copy, while the stored state stays per person.
export const useScaledRangeFilter = ({
  filter,
  handleFilterChanged,
}: Pick<FilterMainComponentProps, 'filter' | 'handleFilterChanged'>): ScaledRangeFilter => {
  const { active, pax, scale, unscaleRange } = usePriceFilterScale(filter.name);

  if (!active) return { filter, handleFilterChanged };

  const data = filter.data as FilterDataRange;

  const handleDisplayFilterChanged: HandleFilterChanged = ([updated]) => {
    const updatedValue = (updated.data as FilterDataRange).value;
    handleFilterChanged([{ ...filter, data: { ...data, value: unscaleRange(updatedValue) } }]);
  };

  return {
    filter: { ...filter, data: scaleRangeData(data, scale) },
    handleFilterChanged: handleDisplayFilterChanged,
    // ×pax units keep every committed total a whole per-person amount.
    roundTo: pax * getRangeRoundTo(data),
  };
};
